// BC-003 · 9 Feb 2026 PM · doctrine lock for the /mira right pane.
//
// The right pane must NEVER revert to the cold-open welcome-mat once
// the conversation has started. Founder bug evidence: a Sardinia trip
// turn produced rich prose but no structured `venue_hints` (beaches
// aren't verified Anchor rows); a follow-up "Thanks" turn returned
// empty `brief_items`/`handoff_moment`. The previous behaviour was to
// `setActiveContext(meta)` unconditionally on every turn, so the
// thin turn wiped the rich prior state and the BasketHeader gate
// fell back to the welcome-mat fallback.
//
// `mergeActiveContext` is the sticky-merge that the page-level
// `onResponseMeta` handler now uses to preserve rich fields across
// thin turns. The contract:
//   • `meta` undefined / null → return `prev` unchanged
//   • `meta.reset === true` → full clear (Start new chat path)
//   • `prev` null → take `meta` wholesale (first turn)
//   • otherwise → merge `meta` over `prev`, preferring new non-empty
//     values but keeping prior values for fields the new turn left
//     empty. `handoff_moment` ratchets ONE-WAY true within a session
//     (once Mira promises the handoff, the CTA stays mounted).

const _NON_EMPTY_ARRAY_FIELDS = [
  "venue_hints",
  "product_hints",
  "brief_items",
  "procedural_steps",
  "mentioned_places",
  "adjacent_capabilities",
  "gathered_details",
];

const _TRUTHY_SCALAR_FIELDS = [
  "primary_city",
  "pillar_name",
  "pillar",
  "pattern",
  "itinerary_plan",
  "sourcing_hint",
  "subcategory",
];

const _isNonEmptyArr = (arr) => Array.isArray(arr) && arr.length > 0;
const _isTruthyScalar = (v) => v !== null && v !== undefined && v !== "";

export function mergeActiveContext(prev, meta) {
  if (!meta) return prev;
  if (meta.reset === true) return null;
  if (!prev) return meta;

  const merged = { ...prev, ...meta };

  for (const field of _NON_EMPTY_ARRAY_FIELDS) {
    merged[field] = _isNonEmptyArr(meta[field])
      ? meta[field]
      : (prev[field] || []);
  }

  for (const field of _TRUTHY_SCALAR_FIELDS) {
    merged[field] = _isTruthyScalar(meta[field])
      ? meta[field]
      : (prev[field] || null);
  }

  // handoff_moment ratchets one-way true within a session
  merged.handoff_moment = !!(meta.handoff_moment || prev.handoff_moment);

  return merged;
}
